import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
} from '@/components';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    REGISTER_EMPTY_COPY,
    type EmployeeRow,
    type RegisterEmptyState,
} from '../../salariesMeta';
import { EmployeeStatusChip } from '../employeeStatusChip';

type EmployeeTableProps = {
    ariaLabel: string;
    rows: EmployeeRow[];
    emptyState: RegisterEmptyState;
    /**
     * The register is still in flight. The table keeps its real column headers
     * and skeletons only the rows — an empty list while loading means "not known
     * yet", never "no employees".
     */
    isLoading?: boolean;
    onEdit: (employeeId: string) => void;
};

const SKELETON_ROW_COUNT = 5;

/**
 * The employee register.
 *
 * A HeroUI Pro `DataGrid`, which renders a real `<table>` underneath — genuine
 * `columnheader`s and a `rowheader` for the name, so the register still reads as
 * a ledger listing. Its accessible name is `aria-label`; DataGrid has no
 * `<caption>` slot. Money is a right-aligned tabular figure; a missing position
 * is an en dash, not a blank cell.
 *
 * A terminated row is de-emphasised but fully legible — the employment is history,
 * not noise — and carries its leaving date under the status chip, because "why is
 * this person greyed out" should not need a second click to answer. DataGrid
 * exposes no per-row className, so the de-emphasis is applied per cell.
 *
 * Pension participation is a word, not a tick: a bare ✓/✗ column leaves a screen
 * reader saying "check" and a sighted reader guessing which way round it runs.
 */
export const EmployeeTable: React.FC<EmployeeTableProps> = ({
    ariaLabel,
    rows,
    emptyState,
    isLoading = false,
    onEdit,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<EmployeeRow>[]>(
        () => [
            {
                id: 'index',
                header: t('salaries.columns.index'),
                align: 'end',
                width: 56,
                cell: (row) => (
                    <span className="tabular-nums text-muted">{row.index}</span>
                ),
            },
            {
                id: 'name',
                header: t('salaries.columns.name'),
                isRowHeader: true,
                minWidth: 180,
                cell: (row) => (
                    <span
                        className={cn(
                            'font-medium',
                            row.isTerminated ? 'text-muted' : 'text-foreground',
                        )}
                    >
                        {row.name}
                    </span>
                ),
            },
            {
                id: 'personalId',
                header: t('salaries.columns.personalId'),
                minWidth: 140,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap tabular-nums',
                            row.isTerminated && 'text-muted',
                        )}
                    >
                        {row.personalId}
                    </span>
                ),
            },
            {
                id: 'position',
                header: t('salaries.columns.position'),
                minWidth: 160,
                cell: (row) => (
                    <span className={cn(row.isTerminated && 'text-muted')}>
                        {row.positionLabel}
                    </span>
                ),
            },
            {
                id: 'hireDate',
                header: t('salaries.columns.hireDate'),
                minWidth: 130,
                // Dates and money keep their symbol and figure on one line — `₾`
                // wrapping onto its own row reads as a stray glyph.
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap tabular-nums',
                            row.isTerminated && 'text-muted',
                        )}
                    >
                        {row.hireDateText}
                    </span>
                ),
            },
            {
                id: 'grossSalary',
                header: t('salaries.columns.grossSalary'),
                align: 'end',
                minWidth: 140,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap font-medium tabular-nums',
                            row.isTerminated ? 'text-muted' : 'text-foreground',
                        )}
                    >
                        {row.grossSalaryText}
                    </span>
                ),
            },
            {
                id: 'pensionParticipant',
                header: t('salaries.columns.pensionParticipant'),
                minWidth: 130,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap',
                            row.isTerminated && 'text-muted',
                        )}
                    >
                        {row.pensionParticipant
                            ? t('salaries.pension.participant')
                            : t('salaries.pension.notParticipant')}
                    </span>
                ),
            },
            {
                id: 'status',
                header: t('salaries.columns.status'),
                minWidth: 140,
                cell: (row) => (
                    <span className="flex flex-col gap-0.5">
                        <EmployeeStatusChip slot={row.id} status={row.status} />
                        {/* "Left on" and "leaving on" are different facts, and
                            only the date can tell them apart: status flips only
                            once the last day has passed, so someone dated to
                            leave in December is ACTIVE today and is charged a
                            full month until they go. Neither gets a third
                            status chip — the date says it. */}
                        {row.terminationDateText ? (
                            <span className="text-xs text-muted">
                                {t(
                                    row.isLeaving
                                        ? 'salaries.leavingOn'
                                        : 'salaries.leftOn',
                                    { date: row.terminationDateText },
                                )}
                            </span>
                        ) : null}
                    </span>
                ),
            },
            {
                id: 'actions',
                header: (
                    <span className="sr-only">
                        {t('salaries.columns.actions')}
                    </span>
                ),
                align: 'end',
                width: 72,
                // The accessible name lives in a visually hidden span, not
                // `aria-label`: HeroUI's Tooltip wrapper drops an `aria-label`
                // set on its trigger Button (verified in the DOM), leaving an
                // icon-only trigger nameless. Text content always survives, and
                // naming the row keeps every Edit button distinct in a screen
                // reader's element list.
                cell: (row) => (
                    <span
                        className="flex items-center justify-end"
                        data-row-actions=""
                    >
                        <Tooltip delay={0}>
                            <Button
                                isIconOnly
                                onPress={() => onEdit(row.id)}
                                size="sm"
                                variant="ghost"
                                {...getNodeTestIdProp(
                                    `salaries-row-edit-${row.id}`,
                                )}
                            >
                                <Icon aria-hidden icon="gravity-ui:pencil" />
                                <span className="sr-only">
                                    {t('salaries.actions.editNamed', {
                                        name: row.name,
                                    })}
                                </span>
                            </Button>
                            <Tooltip.Content>
                                <p>{t('salaries.actions.edit')}</p>
                            </Tooltip.Content>
                        </Tooltip>
                    </span>
                ),
            },
        ],
        [onEdit, t],
    );

    if (!isLoading && emptyState !== 'NONE') {
        const copy = REGISTER_EMPTY_COPY[emptyState];
        return (
            <div
                className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-14 text-center"
                {...getNodeTestIdProp(`salaries-empty-${emptyState}`)}
            >
                <Icon
                    aria-hidden
                    className="size-8 text-muted"
                    icon={copy.icon}
                />
                <span className="text-sm font-medium text-foreground">
                    {t(copy.titleKey)}
                </span>
                <span className="max-w-sm text-sm text-muted">
                    {t(copy.bodyKey)}
                </span>
            </div>
        );
    }

    // `contents` wrapper: `DataGrid` forwards no unknown props, so the
    // register's testid has nowhere to land on the grid itself.
    return (
        <div className="contents" {...getNodeTestIdProp('salaries-table')}>
            {isLoading ? (
                <DataGrid<{ id: string }>
                    aria-label={ariaLabel}
                    className="ledger-data-grid"
                    columns={buildDataGridSkeletonColumns(columns)}
                    contentClassName="min-w-[980px]"
                    data={buildDataGridSkeletonRows(
                        SKELETON_ROW_COUNT,
                        'salaries',
                    )}
                    getRowId={(row) => row.id}
                />
            ) : (
                <DataGrid<EmployeeRow>
                    aria-label={ariaLabel}
                    className="ledger-data-grid"
                    columns={columns}
                    contentClassName="min-w-[980px]"
                    data={rows}
                    getRowId={(row) => row.id}
                />
            )}
        </div>
    );
};
